import React, {
  FunctionComponent,
  SyntheticEvent,
  useState,
  useEffect,
} from 'react';
import { useQuery, useApolloClient } from '@apollo/react-hooks';
import { useParams } from 'react-router-dom';
import withStyles from 'react-jss';

import FormSelect from 'components/FormSelect';
import { ClassProp } from 'helpers/types';
import { useDatePicker } from 'components/DatePicker/DatePicker.context';

import styles from './CashbackSegmentFilter.styles';
import { GET_MERCHANT_OFFER_SEGMENTS } from './CashbackSegmentFilter.query';

type Styles = ClassProp<ReturnType<typeof styles>>;
type Props = {
  classes: Styles;
};

type MatchParams = {
  merchantName: string;
  merchantId: string;
};

type OfferSegment = {
  cashbackPercentage: number;
  cashbackDollarAmt: number;
  segmentId: string;
};

type Option = {
  key: string;
  text: string;
  value: string | null;
};

type Label = {
  children: string;
};

type CashbackSegments = {
  options: Option[];
  label: Label;
  placeholder: string;
};

const CASHBACK_SEGMENTS = {
  options: [
    {
      key: 'all',
      text: 'Show All',
      value: null,
    },
  ],
  label: {
    children: 'Cashback Segments',
  },
  placeholder: 'Show All',
};

const CashbackSegmentFilter: FunctionComponent<Props> = ({ classes }) => {
  const { startDate, endDate } = useDatePicker();
  const params = useParams<MatchParams>();
  const [cashbackSegment, setCashbackSegment] = useState<string | null>(null);
  const [showDropDown, setShowDropDown] = useState<boolean>(false);
  const [cashbackSegments] = useState<CashbackSegments>(CASHBACK_SEGMENTS);
  const client = useApolloClient();

  const { merchantName }: MatchParams = params;

  const { loading, data } = useQuery(GET_MERCHANT_OFFER_SEGMENTS, {
    variables: {
      params: {
        merchantName,
        startDate,
        endDate,
      },
    },
  });

  useEffect(() => {
    client.writeData({ data: { cashbackSegment } });
  }, [cashbackSegment]);

  useEffect(() => {
    if (loading && !data) return;

    const merchantOfferSegments = data?.merchantOfferSegments || [];

    setShowDropDown(merchantOfferSegments.length > 1);

    cashbackSegments.options = cashbackSegments.options.slice(0, 1);

    merchantOfferSegments.forEach((segment: OfferSegment) => {
      const text = segment.cashbackPercentage
        ? `${segment.cashbackPercentage}%`
        : `$${segment.cashbackDollarAmt}`;

      const value = segment.cashbackPercentage
        ? segment.cashbackPercentage
        : segment.cashbackDollarAmt;

      const type = segment.cashbackPercentage ? 'percent' : 'dollar';
      cashbackSegments.options.push({
        text,
        key: segment.segmentId,
        value: `${type}|${value}`,
      });
    });
  }, [loading, data]);

  const onChange = (_: SyntheticEvent<HTMLElement, Event>, { value }: any) => {
    setCashbackSegment(value);
  };

  if (!showDropDown) return null;

  return (
    <div className={classes.container}>
      <span className={classes.label}>Cashback Segment</span>
      <FormSelect
        fluid
        selectOnBlur={false}
        name="cashbackSegment"
        placeholder={cashbackSegments.placeholder}
        options={cashbackSegments.options}
        onChange={onChange}
        value={cashbackSegment}
      />
    </div>
  );
};

export default withStyles(styles)(CashbackSegmentFilter);
